import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type PopoverFooterAlign,
    TextInput,
    Skeleton,
    Popover,
    Button,
    Switch,
    Table,
    Text
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    BellIcon
} from "../../anatomy/icons";

const NAME = "Popover";

const USAGE_CODE = `<Popover
    side="bottom"
    align="start"
>
    <Popover.Trigger>
        <Button
            onPress={() => undefined}
            title="Notifications"
            variant="outline"
            type="neutral"
            size="small"
        />
    </Popover.Trigger>
    <Popover.Content
        description="Choose how you hear about activity."
        title="Notifications"
    >
        <Switch
            onCheckedChange={setIsEmailOn}
            isChecked={isEmailOn}
            title="Email"
        />
        <Popover.Footer>
            <Popover.Close>
                <Button
                    onPress={() => undefined}
                    title="Done"
                />
            </Popover.Close>
        </Popover.Footer>
    </Popover.Content>
</Popover>`;

const CONTROLLED_CODE = `const [isOpen, setIsOpen] = useState(false);

<Popover
    onOpenChange={setIsOpen}
    isOpen={isOpen}
>
    ...
</Popover>`;

const ANCHOR_CODE = `<Popover>
    <Popover.Trigger>Sizes</Popover.Trigger>
    <Table caption="Files">
        <Table.Body>
            <Table.Row>
                <Table.Cell>kapak.png</Table.Cell>
                <Table.Cell>
                    <Popover.Anchor>
                        <Text>1.2 MB</Text>
                    </Popover.Anchor>
                </Table.Cell>
            </Table.Row>
        </Table.Body>
    </Table>
    <Popover.Content
        description="Set the width and height of the image in pixels."
        title="Dimensions"
    >
        <TextInput title="Width" />
        <TextInput title="Height" />
    </Popover.Content>
</Popover>`;

const SIDES = [
    "top",
    "right",
    "bottom",
    "left"
] as const;

const ALIGNS = [
    "start",
    "center",
    "end"
] as const;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const FOOTER_ALIGNS: Array<PopoverFooterAlign> = [
    "end",
    "between",
    "stretch"
];

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("popoverPage.placementTitle"),
        id: "placement"
    },
    {
        title: t("popoverPage.anchorTitle"),
        id: "anchor"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("popoverPage.footerTitle"),
        id: "footer"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("popoverPage.controlledTitle"),
        id: "controlled"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const PopoverPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        isEmailOn,
        setIsEmailOn
    ] = useState(true);

    const [
        isPushOn,
        setIsPushOn
    ] = useState(false);

    const [
        isOpen,
        setIsOpen
    ] = useState(false);

    const [
        lastEvent,
        setLastEvent
    ] = useState("—");

    const handleControlledOpenChange = (isNextOpen: boolean) => {
        setIsOpen(isNextOpen);

        setLastEvent(`onOpenChange(${isNextOpen})`);
    };

    return <Article>
        <ComponentHeader
            lead={t("popoverPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("popoverPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("popoverPage.overviewCaption")}
            >
                <Popover>
                    <Popover.Trigger>
                        <Button
                            title={t("popoverPage.notifications")}
                            onPress={() => undefined}
                            variant="outline"
                            icon={BellIcon}
                            type="neutral"
                            size="small"
                        />
                    </Popover.Trigger>
                    <Popover.Content
                        description={t("popoverPage.notificationsDescription")}
                        title={t("popoverPage.notifications")}
                    >
                        <Switch
                            onCheckedChange={setIsEmailOn}
                            title={t("sample.email")}
                            spreadBehaviour="stretch"
                            isChecked={isEmailOn}
                            isFlip={true}
                        />
                        <Switch
                            onCheckedChange={setIsPushOn}
                            title={t("popoverPage.push")}
                            spreadBehaviour="stretch"
                            isChecked={isPushOn}
                            isFlip={true}
                        />
                        <Popover.Footer>
                            <Popover.Close>
                                <Button
                                    title={t("popoverPage.done")}
                                    onPress={() => undefined}
                                />
                            </Popover.Close>
                        </Popover.Footer>
                    </Popover.Content>
                </Popover>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("popoverPage.placementTitle")}
            id="placement"
        >
            <Paragraph>
                {t("popoverPage.placementText")}
            </Paragraph>
            <Preview
                caption={t("popoverPage.sideCaption")}
            >
                {
                    SIDES.map((side) => <Popover
                        align="center"
                        size="small"
                        side={side}
                        key={side}
                    >
                        <Popover.Trigger>
                            {side}
                        </Popover.Trigger>
                        <Popover.Content
                            isShowClose={false}
                            aria-label={side}
                        >
                            <Text
                                variant="bodyMediumSize"
                                color="high"
                            >
                                {`side="${side}"`}
                            </Text>
                        </Popover.Content>
                    </Popover>)
                }
            </Preview>
            <Preview
                caption={t("popoverPage.alignCaption")}
            >
                {
                    ALIGNS.map((align) => <Popover
                        align={align}
                        size="small"
                        key={align}
                    >
                        <Popover.Trigger>
                            {align}
                        </Popover.Trigger>
                        <Popover.Content
                            isShowClose={false}
                            aria-label={align}
                        >
                            <Text
                                variant="bodyMediumSize"
                                color="high"
                            >
                                {`align="${align}"`}
                            </Text>
                        </Popover.Content>
                    </Popover>)
                }
            </Preview>
        </Section>
        <Section
            title={t("popoverPage.anchorTitle")}
            id="anchor"
        >
            <Paragraph>
                {t("popoverPage.anchorText")}
            </Paragraph>
            <Preview
                caption={t("popoverPage.anchorCaption")}
            >
                <Popover
                    align="start"
                    side="bottom"
                >
                    <Popover.Trigger>
                        {t("popoverPage.anchorButton")}
                    </Popover.Trigger>
                    <Table
                        caption={t("popoverPage.anchorCaptionTable")}
                        isCaptionHidden={true}
                    >
                        <Table.Header>
                            <Table.Row>
                                <Table.Head>
                                    {t("popoverPage.anchorColName")}
                                </Table.Head>
                                <Table.Head>
                                    {t("popoverPage.anchorColType")}
                                </Table.Head>
                                <Table.Head>
                                    {t("popoverPage.anchorColSize")}
                                </Table.Head>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell>
                                    kapak.png
                                </Table.Cell>
                                <Table.Cell>
                                    {t("popoverPage.anchorFileImage")}
                                </Table.Cell>
                                <Table.Cell>
                                    <Popover.Anchor>
                                        <Text
                                            variant="bodyMediumSize"
                                            color="high"
                                        >
                                            {t("popoverPage.anchorSize1")}
                                        </Text>
                                    </Popover.Anchor>
                                </Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>
                                    rapor.pdf
                                </Table.Cell>
                                <Table.Cell>
                                    {t("popoverPage.anchorFileDocument")}
                                </Table.Cell>
                                <Table.Cell>
                                    {t("popoverPage.anchorSize2")}
                                </Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>
                                    notlar.md
                                </Table.Cell>
                                <Table.Cell>
                                    {t("popoverPage.anchorFileText")}
                                </Table.Cell>
                                <Table.Cell>
                                    {t("popoverPage.anchorSize3")}
                                </Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>
                    <Popover.Content
                        description={t("popoverPage.anchorDescription")}
                        title={t("popoverPage.anchorHeading")}
                    >
                        <TextInput
                            title={t("popoverPage.anchorWidth")}
                            spreadBehaviour="stretch"
                            size="small"
                        />
                        <TextInput
                            title={t("popoverPage.anchorHeight")}
                            spreadBehaviour="stretch"
                            size="small"
                        />
                    </Popover.Content>
                </Popover>
            </Preview>
            <CodeBlock
                code={ANCHOR_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("popoverPage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => <Popover
                        size={size}
                        key={size}
                    >
                        <Popover.Trigger>
                            {size}
                        </Popover.Trigger>
                        <Popover.Content
                            description={t("popoverPage.sizeDescription")}
                            title={size}
                        />
                    </Popover>)
                }
            </Preview>
        </Section>
        <Section
            title={t("popoverPage.footerTitle")}
            id="footer"
        >
            <Paragraph>
                {t("popoverPage.footerText")}
            </Paragraph>
            <Preview>
                {
                    FOOTER_ALIGNS.map((footerAlign) => <Popover
                        key={footerAlign}
                    >
                        <Popover.Trigger>
                            {footerAlign}
                        </Popover.Trigger>
                        <Popover.Content
                            description={t("popoverPage.footerDescription")}
                            title={t("popoverPage.footerHeading")}
                        >
                            <Popover.Footer
                                isDivided={footerAlign === "between"}
                                align={footerAlign}
                            >
                                <Button
                                    variant={footerAlign === "between" ? "ghost" : "outline"}
                                    title={t("popoverPage.reset")}
                                    onPress={() => undefined}
                                    type="neutral"
                                />
                                <Popover.Close>
                                    <Button
                                        title={t("sample.action")}
                                        onPress={() => undefined}
                                    />
                                </Popover.Close>
                            </Popover.Footer>
                        </Popover.Content>
                    </Popover>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("popoverPage.statesText")}
            </Paragraph>
            <Preview>
                <Popover>
                    <Popover.Trigger>
                        {t("popoverPage.loading")}
                    </Popover.Trigger>
                    <Popover.Content
                        description={t("popoverPage.loadingDescription")}
                        title={t("popoverPage.share")}
                        isLoading={true}
                    >
                        <Skeleton
                            variant="text"
                            lines={2}
                        />
                    </Popover.Content>
                </Popover>
                <Popover>
                    <Popover.Trigger>
                        <Button
                            title={t("popoverPage.disabled")}
                            onPress={() => undefined}
                            variant="outline"
                            isDisabled={true}
                            type="neutral"
                            size="small"
                        />
                    </Popover.Trigger>
                    <Popover.Content
                        title={t("popoverPage.share")}
                    />
                </Popover>
            </Preview>
        </Section>
        <Section
            title={t("popoverPage.controlledTitle")}
            id="controlled"
        >
            <Paragraph>
                {t("popoverPage.controlledText")}
            </Paragraph>
            <Preview
                caption={t("popoverPage.lastEvent", [
                    lastEvent
                ])}
            >
                <Button
                    title={t("popoverPage.openFromOutside")}
                    onPress={() => setIsOpen(true)}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
                <Popover
                    onOpenChange={handleControlledOpenChange}
                    isOpen={isOpen}
                >
                    <Popover.Trigger>
                        {t("popoverPage.anchor")}
                    </Popover.Trigger>
                    <Popover.Content
                        description={t("popoverPage.controlledDescription")}
                        title={t("popoverPage.controlledHeading")}
                    />
                </Popover>
            </Preview>
            <CodeBlock
                code={CONTROLLED_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("popoverPage.a11yRole"),
                    t("popoverPage.a11yWarning"),
                    t("popoverPage.a11yTrigger"),
                    t("popoverPage.a11yAnchor"),
                    t("popoverPage.a11yFocus"),
                    t("popoverPage.a11yDismiss"),
                    t("popoverPage.a11yModal"),
                    t("popoverPage.a11yTouch"),
                    t("popoverPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        type: "\"top\" | \"right\" | \"bottom\" | \"left\"",
                        description: t("popoverPage.propSide"),
                        defaultValue: "\"bottom\"",
                        name: "side"
                    },
                    {
                        type: "\"start\" | \"center\" | \"end\"",
                        description: t("popoverPage.propAlign"),
                        defaultValue: "\"start\"",
                        name: "align"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("popoverPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("popoverPage.propIsAvoidCollisions"),
                        name: "isAvoidCollisions",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("popoverPage.propIsModal"),
                        defaultValue: "false",
                        name: "isModal",
                        type: "boolean"
                    },
                    {
                        description: t("popoverPage.propTouchBehaviour"),
                        type: "\"bottomSheet\" | \"popover\"",
                        defaultValue: "\"bottomSheet\"",
                        name: "touchBehaviour"
                    },
                    {
                        description: t("popoverPage.propIsOpen"),
                        name: "isOpen",
                        type: "boolean"
                    },
                    {
                        description: t("popoverPage.propIsDefaultOpen"),
                        defaultValue: "false",
                        name: "isDefaultOpen",
                        type: "boolean"
                    },
                    {
                        description: t("popoverPage.propOnOpenChange"),
                        type: "(isOpen: boolean) => void",
                        name: "onOpenChange"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("popoverPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("popoverPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("popoverPage.partsNote")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("popoverPage.propTriggerChildren"),
                        type: "ReactElement | string",
                        name: "Trigger.children"
                    },
                    {
                        description: t("popoverPage.propAnchorChildren"),
                        name: "Anchor.children",
                        type: "ReactNode"
                    },
                    {
                        description: t("popoverPage.propContentTitle"),
                        name: "Content.title",
                        type: "string"
                    },
                    {
                        description: t("popoverPage.propContentDescription"),
                        name: "Content.description",
                        type: "string"
                    },
                    {
                        description: t("popoverPage.propContentAriaLabel"),
                        name: "Content.aria-label",
                        type: "string"
                    },
                    {
                        description: t("popoverPage.propContentIsShowClose"),
                        name: "Content.isShowClose",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("popoverPage.propContentIsLoading"),
                        name: "Content.isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("popoverPage.propContentInitialFocusRef"),
                        type: "RefObject<HostInstance>",
                        name: "Content.initialFocusRef"
                    },
                    {
                        description: t("popoverPage.propFooterAlign"),
                        type: "\"between\" | \"stretch\" | \"end\"",
                        defaultValue: "\"end\"",
                        name: "Footer.align"
                    },
                    {
                        description: t("popoverPage.propFooterIsDivided"),
                        name: "Footer.isDivided",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("popoverPage.propCloseChildren"),
                        name: "Close.children",
                        type: "ReactElement"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("popoverPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default PopoverPage;
